import { useEffect, useState } from "react";
import { API } from "../lib/api";
import { FileText, Video, Image as ImageIcon, Download } from "lucide-react";

async function fetchBlob(path) {
  const res = await fetch(`${API}/files/download?path=${encodeURIComponent(path)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("fetch failed");
  return res.blob();
}

export function SecureImage({ path, size = 64 }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    fetchBlob(path)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (error) {
    return (
      <div className="w-16 h-16 bg-[var(--parchment)] border border-[var(--parchment-2)] flex items-center justify-center text-[var(--muted)] text-xs">
        Erreur
      </div>
    );
  }
  if (!url) return <div className="w-16 h-16 bg-[var(--parchment)] animate-pulse" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block">
      <img
        src={url}
        alt="Document"
        style={{ width: size, height: size }}
        className="object-cover border border-[var(--parchment-2)]"
      />
    </a>
  );
}

function fileIcon(type) {
  if (type.startsWith("video")) return Video;
  if (type === "application/pdf") return FileText;
  return ImageIcon;
}

export function FileChip({ file }) {
  const [busy, setBusy] = useState(false);
  const Icon = fileIcon(file.content_type || "");

  const open = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob(file.path);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-[var(--parchment)] border border-[var(--parchment-2)] hover:border-[var(--maroon)] transition text-[var(--ink)] max-w-[220px]"
      data-testid={`file-chip-${file.path?.split("/").pop()}`}
      title={file.name}
    >
      <Icon size={13} className="shrink-0 text-[var(--maroon)]" />
      <span className="truncate">{file.name || "Document"}</span>
      <Download size={12} className="shrink-0 text-[var(--muted)]" />
    </button>
  );
}

export function FilesList({ files = [], photoPath }) {
  const all = [...files];
  if (photoPath && !all.length) all.push({ path: photoPath, name: "Photo", content_type: "image/jpeg" });
  if (!all.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {all.map((f, i) => (
        <FileChip key={f.path + i} file={f} />
      ))}
    </div>
  );
}
